import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

/**
 * Synchronizes profile data between users and members collections
 * @param {string} uid - User ID
 * @param {Object} profileData - Profile data to sync
 * @param {Object} options - Options for syncing
 * @param {boolean} options.syncToUsers - Whether to sync to users collection (default: true)
 * @param {boolean} options.syncToMembers - Whether to sync to members collection (default: true)
 * @param {Array<string>} options.fields - Specific fields to sync (default: all profile fields)
 */
export async function syncProfileData(uid, profileData, options = {}) {
  const {
    syncToUsers = true,
    syncToMembers = true,
    fields = ['firstName', 'middleName', 'lastName', 'email', 'phone', 'address', 'birthdate', 'birthplace', 'sex', 'civilStatus', 'pwd', 'senior', 'profilePhotoURL']
  } = options;

  const updateData = {
    updatedAt: serverTimestamp()
  };

  // Only include specified fields that exist in profileData
  fields.forEach(field => {
    if (profileData[field] !== undefined) {
      updateData[field] = profileData[field];
    }
  });

  // Add displayName for users collection
  if (updateData.firstName || updateData.lastName) {
    updateData.displayName = `${updateData.firstName || ''} ${updateData.lastName || ''}`.trim();
  }

  const promises = [];

  if (syncToUsers) {
    const userRef = doc(db, "users", uid);
    promises.push(updateDoc(userRef, updateData));
  }

  if (syncToMembers) {
    const memberRef = doc(db, "members", uid);
    promises.push(updateDoc(memberRef, updateData));
  }

  await Promise.all(promises);
}

/**
 * Syncs basic profile data (name, email, phone) to both collections
 */
export async function syncBasicProfile(uid, profileData) {
  return syncProfileData(uid, profileData, {
    fields: ['firstName', 'middleName', 'lastName', 'email', 'phone', 'profilePhotoURL']
  });
}

/**
 * Syncs full profile data to both collections
 */
export async function syncFullProfile(uid, profileData) {
  return syncProfileData(uid, profileData, {
    fields: ['firstName', 'middleName', 'lastName', 'email', 'phone', 'address', 'birthdate', 'birthplace', 'sex', 'civilStatus', 'pwd', 'senior', 'profilePhotoURL']
  });
}
